import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const TransactionDetailsModal = ({ isOpen, onClose, transaction }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen || !transaction) return null;

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Get status display info
  const getStatusInfo = (status, state) => {
    switch (status) {
      case 'completed':
        return {
          icon: <CheckCircleIcon className="h-5 w-5 text-white" />,
          text: 'Completed',
          color: 'text-white',
          bgColor: 'bg-green-900/30'
        };
      case 'pending':
        return {
          icon: <ClockIcon className="h-5 w-5 text-white" />,
          text: 'Pending',
          color: 'text-white',
          bgColor: 'bg-yellow-900/30'
        };
      case 'failed':
        return {
          icon: <XCircleIcon className="h-5 w-5 text-red-400" />,
          text: 'Failed',
          color: 'text-red-400',
          bgColor: 'bg-red-900/30'
        };
      default:
        return {
          icon: <ExclamationTriangleIcon className="h-5 w-5 text-white" />,
          text: 'Unknown',
          color: 'text-white',
          bgColor: 'bg-gray-900/30'
        };
    }
  };

  // Copy to clipboard function
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Get transaction type icon and color
  const getTransactionTypeInfo = (type) => {
    if (type === 'deposit') {
      return {
        icon: <ArrowDownIcon className="h-6 w-6 text-white" />,
        bgColor: 'bg-green-900/30',
        text: 'Deposit'
      };
    } else {
      return {
        icon: <ArrowUpIcon className="h-6 w-6 text-white" />,
        bgColor: 'bg-blue-900/30',
        text: 'Withdrawal'
      };
    }
  };

  const statusInfo = getStatusInfo(transaction.status, transaction.state);
  const typeInfo = getTransactionTypeInfo(transaction.type);

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg mx-auto z-10">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${typeInfo.bgColor}`}>
                  {typeInfo.icon}
                </div>
              </div>
              <div className="ml-3 sm:ml-4">
                <h3 className="text-base sm:text-lg font-semibold text-white">
                  {typeInfo.text} {transaction.coin}
                </h3>
                <p className="text-xs sm:text-sm text-white">Transaction Details</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-white transition-colors p-1"
            >
              <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto max-h-[70vh] sm:max-h-[500px]">
            {/* Transaction Status */}
            <div className="bg-gray-700 rounded-lg p-3 sm:p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-white">Status</h4>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor} ${statusInfo.color}`}>
                  {statusInfo.icon}
                  <span className="ml-2">{statusInfo.text}</span>
                </div>
              </div>
              
              {/* Progress indicator for pending transactions */}
              {transaction.status === 'pending' && (
                <div className="w-full bg-gray-600 rounded-full h-2">
                  <div className="bg-[#0E1696] h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                </div>
              )}
            </div>

            {/* Transaction Details */}
            <div className="bg-gray-700 rounded-lg p-3 sm:p-4">
              <h4 className="text-sm font-medium text-white mb-3 sm:mb-4">Transaction Details</h4>
              <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                {/* Transaction ID */}
                <div className="flex justify-between items-center">
                  <span className="text-white">Transaction ID:</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-white font-mono">#{transaction.id}</span>
                    <button
                      onClick={() => copyToClipboard(transaction.id.toString(), 'Transaction ID')}
                      className="text-white hover:text-white transition-colors"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Amount */}
                <div className="flex justify-between">
                  <span className="text-white">Amount:</span>
                  <span className="text-white font-medium">
                    {transaction.type === 'deposit' ? '+' : '-'}
                    {parseFloat(transaction.amount).toFixed(8)} {transaction.coin}
                  </span>
                </div>

                {/* Network/Blockchain */}
                {transaction.blockchainCurrency && (
                  <div className="flex justify-between">
                    <span className="text-white">Network:</span>
                    <span className="text-white">{transaction.blockchainCurrency}</span>
                  </div>
                )}

                {/* Destination Address */}
                {transaction.address && transaction.address !== 'N/A' && (
                  <div className="flex justify-between items-start">
                    <span className="text-white">Destination:</span>
                    <div className="flex items-center space-x-2 max-w-[200px]">
                      <span className="text-white font-mono text-xs truncate">
                        {transaction.address}
                      </span>
                      <button
                        onClick={() => copyToClipboard(transaction.address, 'Address')}
                        className="text-white hover:text-white transition-colors flex-shrink-0"
                      >
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* TX Hash (if available) */}
                {transaction.txHash && transaction.txHash !== 'N/A' && (
                  <div className="flex justify-between items-start">
                    <span className="text-white">TX Hash:</span>
                    <div className="flex items-center space-x-2 max-w-[200px]">
                      <span className="text-white font-mono text-xs truncate">
                        {transaction.txHash}
                      </span>
                      <button
                        onClick={() => copyToClipboard(transaction.txHash, 'TX Hash')}
                        className="text-white hover:text-white transition-colors flex-shrink-0"
                      >
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Paygate ID */}
                {transaction.paygateId && transaction.paygateId !== 'N/A' && (
                  <div className="flex justify-between">
                    <span className="text-white">Paygate:</span>
                    <span className="text-white">{transaction.paygateId}</span>
                  </div>
                )}

                {/* Confirmation Status */}
                {transaction.confirmed !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-white">Confirmed:</span>
                    <span className={`${transaction.confirmed ? 'text-white' : 'text-white'}`}>
                      {transaction.confirmed ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}

                {/* Confirmations Count */}
                {transaction.confirmationsCount !== undefined && transaction.confirmationsRequired !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-white">Confirmations:</span>
                    <span className="text-white">
                      {transaction.confirmationsCount}/{transaction.confirmationsRequired}
                    </span>
                  </div>
                )}

                {/* Created Time */}
                <div className="flex justify-between">
                  <span className="text-white">Created:</span>
                  <span className="text-white text-xs">
                    {formatTimestamp(transaction.timestamp)}
                  </span>
                </div>

                {/* Updated Time (if different from created) */}
                {transaction.updated && transaction.updated !== transaction.timestamp && (
                  <div className="flex justify-between">
                    <span className="text-white">Updated:</span>
                    <span className="text-white text-xs">
                      {formatTimestamp(transaction.updated)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Data */}
            {transaction.data && Object.keys(transaction.data).length > 0 && (
              <div className="bg-gray-700 rounded-lg p-4">
                <h4 className="text-sm font-medium text-white mb-3">Additional Data</h4>
                <div className="space-y-2 text-sm">
                  {Object.entries(transaction.data).map(([key, value]) => {
                    let displayValue;
                    if (typeof value === 'object') {
                      displayValue = JSON.stringify(value);
                    } else {
                      const strValue = String(value);
                      if (strValue.length > 6) {
                        // Show first 3 and last 3 characters, hide the middle with ellipsis
                        displayValue = `${strValue.slice(0, 3)}...${strValue.slice(-3)}`;
                      } else {
                        displayValue = strValue;
                      }
                    }
                    return (
                      <div key={key} className="flex justify-between items-start">
                        <span className="text-white capitalize">
                          {key.replace(/_/g, ' ')}:
                        </span>
                        <div className="flex items-center space-x-2 max-w-[200px]">
                          <span className="text-white font-mono text-xs truncate">
                            {displayValue}
                          </span>
                          <button
                            onClick={() => copyToClipboard(String(value), key.replace(/_/g, ' '))}
                            className="text-white hover:text-white transition-colors flex-shrink-0"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Status-specific messages */}
            {transaction.status === 'pending' && (
              <div className="bg-yellow-900 bg-opacity-20 border border-[#2A356D] rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <ClockIcon className="w-5 h-5 text-white mt-0.5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-white">
                      Your {transaction.type} is being processed. This usually takes 5-30 minutes depending on network congestion.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {transaction.status === 'completed' && (
              <div className="bg-green-900 bg-opacity-20 border border-green-700 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="w-5 h-5 text-white mt-0.5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-300">
                      Your {transaction.type} has been completed successfully and confirmed on the blockchain.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-750 border-t border-gray-700">
            <button
              onClick={onClose}
              className="w-full py-2 px-4 bg-[#0E1696] hover:bg-blue-700 rounded-lg !text-white font-medium transition-colors text-sm sm:text-base"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal;
